// Enhanced Company Management Service for ERP System
import { supabase } from '@/lib/supabaseClient';
import authService from './authService';

export interface Company {
  id: string;
  name: string;
  logo_url?: string;
  code?: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  industry?: string;
  timezone?: string;
  currency?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: string;
}

export interface UserCompanyRole {
  id: string;
  user_id: string;
  company_id: string;
  role: string;
  scope: string;
  permissions: Record<string, any>;
  is_active: boolean;
  granted_by?: string;
  granted_at: string;
  expires_at?: string;
}

export interface CompanyStats {
  total_users: number;
  active_users: number;
  total_products: number;
  total_inventory_value: number;
  recent_activity: number;
}

class CompanyService {
  // ====================================================================
  // COMPANY CRUD OPERATIONS
  // ====================================================================

  async getAllCompanies(signal?: AbortSignal): Promise<Company[]> {
    try {
      let query = supabase
        .from('companies')
        .select('*')
        .order('name');
      
      if (signal) {
        query = query.abortSignal(signal);
      }
      
      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  }

  async getUserCompanies(userId?: string, signal?: AbortSignal): Promise<Company[]> {
    try {
      // Ensure auth is hydrated (handles hard refresh where auth may be late)
      let currentUser = authService.getCurrentUser();
      let userProfile = authService.getUserProfile();
      if (!currentUser || !userProfile) {
        let retries = 0;
        const maxRetries = 100; // ~10s to better tolerate F5 hydration
        while (retries < maxRetries && (!currentUser || !userProfile)) {
          await new Promise((r) => setTimeout(r, 100));
          currentUser = authService.getCurrentUser();
          userProfile = authService.getUserProfile();
          retries++;
        }
      }

      const targetUserId = userId || currentUser?.id;
      
      if (!targetUserId) {
        throw new Error('用戶未登入');
      }

      if (authService.isAdmin()) {
        return this.getAllCompanies(signal);
      }

      userProfile = authService.getUserProfile();
      if (!userProfile || !Array.isArray(userProfile.company_ids) || userProfile.company_ids.length === 0) {
        console.log('User has no assigned companies');
        return [];
      }

      if (!userProfile.is_active) {
        console.warn('User account is not active');
        throw new Error('帳戶已被停用');
      }

      // Prepare both numeric and string ID lists
      const numericIds = userProfile.company_ids
        .map((v: any) => Number(String(v).trim()))
        .filter((n: any) => Number.isFinite(n)) as number[];

      const stringIds = userProfile.company_ids.map((v: any) => String(v));

      const tryFetch = async (ids: Array<number | string>): Promise<Company[]> => {
        try {
          // First attempt with is_active filter
          let firstQuery = supabase
            .from('companies')
            .select('*')
            .in('id', ids as any)
            .eq('is_active', true)
            .order('name');
          
          if (signal) {
            firstQuery = firstQuery.abortSignal(signal);
          }
          
          const first = await firstQuery;

          if (!first.error) {
            return first.data || [];
          }

          if (first.error.code === '42703' && (first.error.message || '').includes('is_active')) {
            // Retry without is_active filter if column doesn't exist
            let secondQuery = supabase
              .from('companies')
              .select('*')
              .in('id', ids as any)
              .order('name');
            
            if (signal) {
              secondQuery = secondQuery.abortSignal(signal);
            }
            
            const second = await secondQuery;
            if (second.error) throw second.error;
            return second.data || [];
          }

          // Other errors
          throw first.error;
        } catch (err) {
          throw err;
        }
      };

      // Prefer numeric IDs first when available
      let companies: Company[] = [];
      if (numericIds.length > 0) {
        try {
          companies = await tryFetch(numericIds);
        } catch (e) {
          console.warn('Numeric ID fetch failed, will try string IDs if available', e);
        }
      }

      if ((!companies || companies.length === 0) && stringIds.length > 0) {
        try {
          companies = await tryFetch(stringIds);
        } catch (e) {
          console.error('String ID fetch failed', e);
          throw e;
        }
      }

      return companies || [];
    } catch (error) {
      console.error('Error fetching user companies:', error);
      throw error;
    }
  }

  async getCompanyById(companyId: string, signal?: AbortSignal): Promise<Company | null> {
    try {
      let query = supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();
      
      if (signal) {
        query = query.abortSignal(signal);
      }
      
      const { data, error } = await query;

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      return data || null;
    } catch (error) {
      console.error('Error fetching company:', error);
      throw error;
    }
  }

  async createCompany(companyData: Omit<Company, 'id' | 'created_at' | 'updated_at'>): Promise<Company> {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('用戶未登入');
      }

      // Generate a unique company code if not provided
      const code = companyData.code || await this.generateCompanyCode(companyData.name);

      const { data, error } = await supabase
        .from('companies')
        .insert({
          ...companyData,
          code,
          created_by: currentUser.id,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;

      // Create admin role for the creator in this company
      await this.assignUserToCompany(currentUser.id, data.id, 'admin');

      return data;
    } catch (error) {
      console.error('Error creating company:', error);
      throw error;
    }
  }

  async updateCompany(companyId: string, updates: Partial<Company>): Promise<Company> {
    try {
      console.log('CompanyService.updateCompany called with:', { companyId, updates });
      
      // Remove read-only fields and drop empty-string fields to reduce schema drift impact
      const { id, created_at, created_by, ...rest } = updates;
      const baseData: Record<string, any> = {};
      Object.entries(rest).forEach(([key, value]) => {
        // Only include fields that are not undefined and not strictly null
        // Avoid sending empty strings which may correspond to non-existing columns in remote schema
        if (value !== undefined && value !== null && value !== '') {
          baseData[key] = value;
        }
      });
      
      // Always try to set updated_at, but be ready to drop it if schema says missing
      baseData.updated_at = new Date().toISOString();

      console.log('Sending update data:', baseData);

      let payload = { ...baseData };
      const maxRetries = 6;
      let attempt = 0;

      while (attempt < maxRetries) {
        const { data, error } = await supabase
          .from('companies')
          .update(payload)
          .eq('id', companyId)
          .select()
          .single();

        if (!error) {
          console.log('Company update successful:', data);
          return data as Company;
        }

        // Handle schema cache or missing column errors by dropping the offending field and retrying
        if (error.code === 'PGRST204' && typeof error.message === 'string') {
          const match = error.message.match(/\'([^']+)\' column/);
          const missingColumn = match?.[1];
          if (missingColumn && payload.hasOwnProperty(missingColumn)) {
            console.warn(`Schema cache drift detected. Removing field '${missingColumn}' and retrying...`);
            delete (payload as any)[missingColumn];
            attempt++;
            continue;
          }
        }

        console.error('Supabase update error:', error);
        throw error;
      }

      throw new Error('更新公司失敗：重試次數已達上限');
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  }

  async deleteCompany(companyId: string): Promise<void> {
    try {
      // Soft delete - mark as inactive instead of actual deletion
      const { error } = await supabase
        .from('companies')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', companyId);

      if (error) throw error;

      // Also deactivate all user roles for this company
      await supabase
        .from('user_roles')
        .update({ is_active: false })
        .eq('scope', `company:${companyId}`);

    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }

  // ====================================================================
  // USER-COMPANY RELATIONSHIP MANAGEMENT
  // ====================================================================

  async assignUserToCompany(
    userId: string, 
    companyId: string, 
    role: string = 'user',
    permissions?: Record<string, any>
  ): Promise<void> {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('用戶未登入');
      }

      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role: role,
          scope: `company:${companyId}`,
          permissions: permissions || {},
          granted_by: currentUser.id,
          is_active: true,
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error assigning user to company:', error);
      throw error;
    }
  }

  async removeUserFromCompany(userId: string, companyId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_roles')
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('scope', `company:${companyId}`);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing user from company:', error);
      throw error;
    }
  }

  async updateUserCompanyRole(
    userId: string, 
    companyId: string, 
    newRole: string,
    permissions?: Record<string, any>
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_roles')
        .update({ 
          role: newRole,
          permissions: permissions || {},
        })
        .eq('user_id', userId)
        .eq('scope', `company:${companyId}`)
        .eq('is_active', true);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating user company role:', error);
      throw error;
    }
  }

  async getCompanyUsers(companyId: string, signal?: AbortSignal): Promise<Array<{
    user_id: string;
    email: string;
    role: string;
    permissions: Record<string, any>;
    is_active: boolean;
    granted_at: string;
  }>> {
    try {
      let query = supabase
        .from('user_roles')
        .select(`
          user_id,
          role,
          permissions,
          is_active,
          granted_at,
          profiles:user_id (
            email
          )
        `)
        .eq('scope', `company:${companyId}`)
        .eq('is_active', true);
      
      // Add abort signal if provided
      if (signal) {
        query = query.abortSignal(signal);
      }
      
      const { data, error } = await query;

      if (error) throw error;

      return (data || []).map(item => ({
        user_id: item.user_id,
        email: (item.profiles as any)?.email || 'Unknown',
        role: item.role,
        permissions: item.permissions,
        is_active: item.is_active,
        granted_at: item.granted_at,
      }));
    } catch (error) {
      console.error('Error fetching company users:', error);
      throw error;
    }
  }

  // ====================================================================
  // COMPANY SWITCHING & MULTI-TENANT SUPPORT
  // ====================================================================

  async switchCompany(companyId: string, signal?: AbortSignal): Promise<void> {
    try {
      // Verify user has access to this company
      const userCompanies = await this.getUserCompanies(undefined, signal);
      const hasAccess = userCompanies.some(company => company.id === companyId);

      if (!hasAccess && !authService.isAdmin()) {
        throw new Error('您沒有存取此公司的權限');
      }

      // Get company details
      const company = await this.getCompanyById(companyId, signal);
      if (!company) {
        throw new Error('公司不存在');
      }

      // Update localStorage for immediate UI updates
      localStorage.setItem('selectedCompanyId', companyId);
      localStorage.setItem('selectedCompanyName', company.name);

      // Get user's role in this company
      const userRole = await this.getUserRoleInCompany(companyId);
      localStorage.setItem('currentCompanyRole', userRole || 'user');

      // Update session in database
      await this.updateSessionCompany(companyId);

    } catch (error) {
      console.error('Error switching company:', error);
      throw error;
    }
  }

  async getCurrentCompany(signal?: AbortSignal): Promise<Company | null> {
    try {
      const companyId = localStorage.getItem('selectedCompanyId');
      if (!companyId) return null;

      return await this.getCompanyById(companyId, signal);
    } catch (error) {
      console.error('Error getting current company:', error);
      return null;
    }
  }

  async getUserRoleInCompany(companyId: string, userId?: string): Promise<string | null> {
    try {
      const currentUser = authService.getCurrentUser();
      const targetUserId = userId || currentUser?.id;
      
      if (!targetUserId) return null;

      // Check if user is global admin
      if (authService.isAdmin()) {
        return 'admin';
      }

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', targetUserId)
        .eq('scope', `company:${companyId}`)
        .eq('is_active', true)
        .order('granted_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data?.role || null;
    } catch (error) {
      console.error('Error getting user role in company:', error);
      return null;
    }
  }

  // ====================================================================
  // COMPANY STATISTICS & ANALYTICS
  // ====================================================================

  async getCompanyStats(companyId: string, signal?: AbortSignal): Promise<CompanyStats> {
    try {
      // This would typically involve multiple queries to different tables
      // For now, returning mock data structure
      const stats: CompanyStats = {
        total_users: 0,
        active_users: 0,
        total_products: 0,
        total_inventory_value: 0,
        recent_activity: 0,
      };

      // Get user count for this company
      const users = await this.getCompanyUsers(companyId, signal);
      stats.total_users = users.length;
      stats.active_users = users.filter(u => u.is_active).length;

      // TODO: Add queries for products, inventory, and activity
      // These would depend on the specific product and inventory table structures

      return stats;
    } catch (error) {
      console.error('Error getting company stats:', error);
      throw error;
    }
  }

  // ====================================================================
  // UTILITY METHODS
  // ====================================================================

  private async generateCompanyCode(companyName: string): Promise<string> {
    try {
      // Generate a base code from company name
      const baseCode = companyName
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .substring(0, 4)
        .padEnd(4, 'X');

      // Check if code already exists
      const { data: existingCompanies } = await supabase
        .from('companies')
        .select('code')
        .like('code', `${baseCode}%`);

      if (!existingCompanies || existingCompanies.length === 0) {
        return baseCode;
      }

      // Generate unique code with number suffix
      let counter = 1;
      let uniqueCode = `${baseCode}${counter.toString().padStart(2, '0')}`;
      
      while (existingCompanies.some(c => c.code === uniqueCode)) {
        counter++;
        uniqueCode = `${baseCode}${counter.toString().padStart(2, '0')}`;
      }

      return uniqueCode;
    } catch (error) {
      console.error('Error generating company code:', error);
      // Fallback to timestamp-based code
      return `COMP${Date.now().toString().slice(-6)}`;
    }
  }

  private async updateSessionCompany(companyId: string): Promise<void> {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) return;

      // Update current active session
      await supabase
        .from('user_sessions')
        .update({ 
          company_id: companyId,
          last_activity: new Date().toISOString(),
        })
        .eq('user_id', currentUser.id)
        .eq('is_active', true);

    } catch (error) {
      console.error('Error updating session company:', error);
      // Non-critical error, don't throw
    }
  }

  async uploadCompanyLogo(companyId: string, file: File): Promise<string> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${companyId}-${Date.now()}.${fileExt}`;
      const filePath = `company-logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      if (!urlData) {
        throw new Error('無法獲取文件公開URL');
      }

      // Update company with new logo URL
      await this.updateCompany(companyId, { logo_url: urlData.publicUrl });

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading company logo:', error);
      throw error;
    }
  }

  async searchCompanies(query: string): Promise<Company[]> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .or(`name.ilike.%${query}%,code.ilike.%${query}%,description.ilike.%${query}%`)
        .eq('is_active', true)
        .order('name')
        .limit(20);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching companies:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const companyService = new CompanyService();
export default companyService;
